import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Image,
  FileText,
  Users,
  MessageSquare,
  HelpCircle,
  Mail,
  Shield,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/hooks";
import { auth } from "@/lib/firebase";
import { getOptimizedImageUrl } from "@/lib/cloudinary";
import { siteSettingsService } from "@/services/firebase";
import { cn } from "@/lib/utils";
import toast from "react-hot-toast";

const menuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin", exact: true },
  { icon: Image, label: "Hero", path: "/admin/hero" },
  { icon: FileText, label: "Contenu", path: "/admin/content" },
  { icon: Users, label: "Artisans", path: "/admin/artisans" },
  { icon: MessageSquare, label: "Témoignages", path: "/admin/testimonials" },
  { icon: HelpCircle, label: "FAQ", path: "/admin/faq" },
  { icon: Mail, label: "Messages", path: "/admin/messages" },
  { icon: Shield, label: "Admins", path: "/admin/admins" },
  { icon: Settings, label: "Paramètres", path: "/admin/settings" },
];

// AJOUT : Définition de l'interface pour TypeScript
interface AdminSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

export function AdminSidebar({ isCollapsed, setIsCollapsed }: AdminSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logo, setLogo] = useState<string | undefined>();
  const location = useLocation();
  const { admin } = useAuth();

  useEffect(() => {
    const unsubscribe = siteSettingsService.subscribe((settings) => {
      setLogo(settings?.logo);
    });
    return unsubscribe;
  }, []);

  const handleLogout = async () => {
    await auth.signOut();
    toast.success("Déconnecté avec succès");
    window.location.href = "/admin/login";
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className={cn("flex items-center gap-3 px-4 py-6", isCollapsed && "justify-center")}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden bg-white">
          <img src={logo ? getOptimizedImageUrl(logo, ["w_80", "h_80", "c_fit"]) : "/logo.png"} alt="Logo" className="w-full h-full object-contain p-1" />
        </div>
        {!isCollapsed && (
          <div>
            <h1 className="font-bold text-white">Allo Artisan</h1>
            <p className="text-xs text-white/50">Dashboard</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const isActive = item.exact
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200",
                isActive
                  ? "bg-primary text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              )}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="p-4 border-t border-white/10">
        {!isCollapsed && admin && (
          <div className="mb-3 px-3">
            <p className="text-white/80 text-sm font-medium">{admin.displayName || admin.email}</p>
            <p className="text-white/40 text-xs capitalize">{admin.role}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors w-full",
            isCollapsed && "justify-center"
          )}
        >
          <LogOut className="w-5 h-5" />
          {!isCollapsed && <span className="text-sm font-medium">Déconnexion</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: isCollapsed ? 80 : 256 }} // Utilise 80px (plie) ou 256px (deplié)
        className="hidden lg:flex flex-col bg-dark h-screen fixed left-0 top-0 z-40 border-r border-white/5"
      >
        <SidebarContent />
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white shadow-lg"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </motion.aside>

      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 bg-white rounded-lg flex items-center justify-center overflow-hidden border border-gray-200"
      >
        <img src={logo ? getOptimizedImageUrl(logo, ["w_80", "h_80", "c_fit"]) : "/logo.png"} alt="Logo" className="w-full h-full object-contain p-1" />
      </button>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -256 }}
              animate={{ x: 0 }}
              exit={{ x: -256 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-dark z-50 flex flex-col"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
